import type { TFunction } from 'i18next';
import { DynamicIcon } from 'lucide-react/dynamic';
import React from 'react';
import { Badge, Modal, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { Card as CardType, GoalWithStatus } from '../../service/api';
import Coin from '../Coin';
import ItemCard from '../ItemCard';

interface GoalDetailsModalProps {
  show: boolean;
  onHide: () => void;
  goalData: GoalWithStatus | null;
}

const GoalDetailsModal: React.FC<GoalDetailsModalProps> = ({
  show,
  onHide,
  goalData,
}) => {
  const { t, i18n } = useTranslation();

  if (!goalData) return null;

  const { goal, status, count } = goalData;

  const sequenceTypeBadge =
    goal.config.sequenceType === 'sequential'
      ? { text: t('goals.inRow'), variant: 'warning' }
      : { text: t('goals.total'), variant: 'info' };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="fs-3"
      contentClassName="position-relative"
      backdrop="static"
    >
      <Modal.Dialog
        className="m-0"
        style={{
          backgroundColor: 'rgba(6, 56, 96, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <div
          className="position-absolute top-0 start-50 translate-middle d-flex align-items-center justify-content-center rounded-circle bg-primary"
          style={{
            width: '64px',
            height: '64px',
            border: '4px solid rgba(6, 56, 96, 0.95)',
            zIndex: 1,
          }}
        >
          <DynamicIcon
            name={goal.icon}
            size={32}
            strokeWidth={1.5}
            className="text-white"
          />
        </div>

        <Modal.Header
          closeButton
          closeVariant="white"
          className="border-0 pt-5 pb-3"
          style={{ backgroundColor: 'transparent' }}
        >
          <Modal.Title className="fs-1 text-white fw-bold w-100 text-center">
            {goal.title}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="text-white px-4 pb-4"
          style={{ backgroundColor: 'transparent' }}
        >
          <div className="d-flex justify-content-center gap-2 mb-4">
            <Badge
              bg={sequenceTypeBadge.variant}
              className="fs-6 px-3 py-2"
              style={{ fontSize: '0.875rem !important' }}
            >
              {sequenceTypeBadge.text}
            </Badge>
            {goal.config.repeatable && (
              <Badge
                bg="success"
                className="fs-6 px-3 py-2"
                style={{ fontSize: '0.875rem !important' }}
              >
                {t('goals.repeatable')}
              </Badge>
            )}
            {status.isCompleted && (
              <Badge
                bg="light"
                text="dark"
                className="fs-6 px-3 py-2"
                style={{ fontSize: '0.875rem !important' }}
              >
                ✓ {t('goals.completed')}
              </Badge>
            )}
          </div>

          {goal.description && (
            <div className="mb-4">
              <h6 className="text-white-50 mb-3 fs-4">
                {t('common.description')}
              </h6>
              <p className="mb-0 fs-5">{goal.description}</p>
            </div>
          )}

          <div className="mb-4">
            <h6 className="text-white-50 mb-3 fs-4">{t('goals.progress')}</h6>
            <div className="mb-3">
              <ProgressBar
                now={status.percentage}
                variant={status.isCompleted ? 'success' : 'primary'}
                style={{ height: '32px' }}
                className="rounded-3"
              />
            </div>
            <div className="d-flex justify-content-between text-white-50 fs-5">
              <span>
                {t('goals.current')}: {status.current}
              </span>
              <span>
                {t('goals.required')}: {status.required}
              </span>
            </div>
          </div>

          <div className="mb-4">
            <h6 className="text-white-50 mb-3 fs-4">{t('common.reward')}</h6>
            <div
              className="d-flex align-items-center justify-content-center gap-3 p-4 rounded-3"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
            >
              {typeof goal.rewardCard !== 'string' &&
                (goal.rewardCard as CardType).actions?.map((action, idx) =>
                  action.type === 'sticker' ? (
                    <React.Fragment key={idx}>
                      <Coin size="lg" />
                      <span className="fw-bold fs-2 text-warning">
                        {action.amount} {t('common.stickers')}
                      </span>
                    </React.Fragment>
                  ) : action.type === 'item' ? (
                    action.item && (
                      <ItemCard
                        key={idx}
                        item={action.item}
                        quantity={action.amount}
                        size="lg"
                        showPrice={false}
                        showTitle={true}
                      />
                    )
                  ) : null,
                )}
            </div>
          </div>

          {status.isCompleted && count > 1 && (
            <div className="mb-4">
              <h6 className="text-white-50 mb-3 fs-4">
                {t('goals.statistics')}
              </h6>
              <div
                className="p-4 rounded-3"
                style={{ backgroundColor: 'rgba(40, 167, 69, 0.2)' }}
              >
                <div className="text-center">
                  <div className="fs-1 fw-bold text-success mb-2">{count}</div>
                  <div className="fs-5">
                    {getTimesLabel(count, t, i18n.language)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal.Dialog>
    </Modal>
  );
};

const getTimesLabel = (
  count: number,
  t: TFunction,
  language: string,
): string => {
  if (language === 'en') {
    return t('goals.timesCompleted', { count });
  }
  const pluralRules = new Intl.PluralRules(language, { type: 'cardinal' });
  const rule = pluralRules.select(count);
  return t(`goals.timesCompleted_${rule}`, { count });
};

export default GoalDetailsModal;
